import { Form, Typography } from '@loophealth/loop-ui-web-library';
import React, { useState } from 'react';
import {
    StyledBenefitPerLifeSelectionModalBody,
    StyledBenefitPerLifeSelectionModalBodyStepTwo,
    StyledFieldsGrid,
    StyledBnenefitPerLifeScrollContainer,
    StyledExpandedContent,
    StyledExpandIcon,
    StyledMemberCard,
    StyledMemberDetails,
    StyledMemberHeader,
    StyledMemberInfo
} from './styles';
import ProfileIcon from '../../atoms/ProfileIcon';
import theme from '../../../theme';
import {
    capitalizeFirstLetter,
    formatFullName,
    getIconMapping
} from '../../../utils/common';
import { ArrowUpEmerald } from '../../../assets/img';
import { IPurchaseAttributes } from '../../../redux/slices/ModularEnrolmentSlice/types';
import { AttributeType } from '../../../redux/slices/PolicyListSlice/types';
import { IBenefitPerLifeMemberConfig, IBenefitPerLifeMemberValue } from './types';

interface PurchaseAttributesStep2Props {
    values: Record<string, IBenefitPerLifeMemberValue> ;
    members: IBenefitPerLifeMemberConfig[];
    purchaseAttributes: IPurchaseAttributes;
    formValues: Record<string, Record<string, string>>;
    setFormValues: (value: Record<string, IBenefitPerLifeMemberValue> ) => void;
    handleFormChange: (fieldKey: string, value: string, userId: string, category: string) => void;
    errorStatus: boolean;
    errorMessage: string;
}

const PurchaseAttributesStep2: React.FC<PurchaseAttributesStep2Props> = ({
    values,
    members,
    purchaseAttributes,
    formValues,
    handleFormChange,
    errorStatus,
    errorMessage
}) => {
    const selectedMembers = Object.keys(values).filter((id) => values[id].checked);
    const selectedMemberDetails = members.filter((member) => selectedMembers.includes(member.data.id));
    const [expandedMemberId, setExpandedMemberId] = useState<string | null>(() => {
        return selectedMemberDetails.length > 0 ? selectedMemberDetails[0].data.id : null;
    });

    const handleExpand = (id: string) => {
        setExpandedMemberId(expandedMemberId === id ? null : id);
    };

    const getPricingStructure = (
        id: string,
        planArray: Array<{ name: string; value: string; price: number | string }>
    ): string => {
        const userPlan = values[id]?.value;
        if (!userPlan) return '';
        const planDetails = planArray.find((plan) => plan.value === userPlan);
        return planDetails?.name ?? '';
    };

    return (
        <StyledBenefitPerLifeSelectionModalBody>
            <Typography variant="medium">
                <Typography variant="medium" weight="semiBold">
                    Step 2 :
                </Typography>{' '}
                Add Required Details for Selected Member
            </Typography>
            <StyledBnenefitPerLifeScrollContainer
                $total={selectedMemberDetails.length}
                data-testid="purchase-attributes-step2-scroll-container"
            >
                <StyledBenefitPerLifeSelectionModalBodyStepTwo
                    $total={selectedMemberDetails.length}
                    data-testid="purchase-attributes-step2-member-list"
                >
                    {selectedMemberDetails.map(({ data, dropdownOptions }, memberIndex) => (
                        <StyledExpandedContent
                            key={data.id}
                            data-testid={`purchase-attributes-step2-expanded-content-${memberIndex}`}
                        >
                            <StyledMemberCard
                                data-testid={`purchase-attributes-step2-member-card-${memberIndex}`}
                            >
                                <StyledMemberHeader
                                    $expanded={expandedMemberId === data.id}
                                    onClick={() => handleExpand(data.id)}
                                    data-testid={`purchase-attributes-step2-member-header-${memberIndex}`}
                                >
                                    <StyledMemberInfo>
                                        <ProfileIcon
                                            icon={getIconMapping(data.relationship, data.gender)}
                                            bgColor={theme.colors.coverYellow}
                                        />
                                        <StyledMemberDetails
                                            data-testid={`purchase-attributes-step2-member-details-${memberIndex}`}
                                        >
                                            <Typography variant="medium" weight="semiBold">
                                                {formatFullName(data.firstName, data.lastName)}
                                            </Typography>
                                            <Typography variant="extraSmall" weight="medium" color="secondary">
                                                {capitalizeFirstLetter(data.relationship)} •{' '}
                                                {getPricingStructure(data.id, dropdownOptions)}
                                            </Typography>
                                        </StyledMemberDetails>
                                    </StyledMemberInfo>
                                    <StyledExpandIcon
                                        src={ArrowUpEmerald}
                                        $expanded={expandedMemberId === data.id}
                                        alt="Expand"
                                        data-testid={`purchase-attributes-step2-expand-icon-${memberIndex}`}
                                    />
                                </StyledMemberHeader>
                            </StyledMemberCard>
                            {expandedMemberId === data.id && (
                                <StyledFieldsGrid
                                    data-testid={`purchase-attributes-step2-fields-grid-${memberIndex}`}
                                >
                                    {Object.keys(purchaseAttributes).map((fieldKey, fieldIndex) => {
                                        const attribute = purchaseAttributes[fieldKey];
                                        const items = attribute?.options ?? [];

                                        return (
                                            <Form.FormField
                                                id={fieldKey}
                                                key={fieldKey}
                                                label={attribute?.label || ''}
                                                required={attribute?.required}
                                                data-testid=
                                                    {`purchase-attributes-step2-field-${memberIndex}-${fieldIndex}`}
                                            >
                                                {attribute?.type ===
                                                    AttributeType.SingleSelect && attribute?.options && (
                                                    <Form.Dropdown
                                                        searchable={true}
                                                        placeholder={attribute['placeHolder']?.toLowerCase()}
                                                        listItems={items.map((opt) => ({ name: opt, value: opt }))}
                                                        selectedItem={formValues[data.id]?.[fieldKey] || ''}
                                                        onClick={(value) =>
                                                            handleFormChange(
                                                                fieldKey,
                                                                value,
                                                                data.id,
                                                                attribute?.category || ''
                                                            )
                                                        }
                                                        autoDisplayItems={false}
                                                        data-testid=
                                                            {`purchase-attributes-step2-dropdown-
                                                                ${memberIndex}-${fieldIndex}`}
                                                    />
                                                )}

                                                {attribute?.type === AttributeType.Text && (
                                                    <Form.Input
                                                        value={formValues[data.id]?.[fieldKey] || ''}
                                                        onChange={(e) =>
                                                            handleFormChange(
                                                                fieldKey,
                                                                e.target.value,
                                                                data.id,
                                                                attribute?.category || ''
                                                            )
                                                        }
                                                        suffix={attribute?.suffix || ''}
                                                        prefix={attribute?.prefix || ''}
                                                        placeholder={attribute['placeHolder']?.toLowerCase()}
                                                        type="text"
                                                        error={errorStatus ? errorMessage : ''}
                                                        data-testid=
                                                            {`purchase-attributes-step2-input-
                                                                ${memberIndex}-${fieldIndex}`}
                                                    />
                                                )}
                                            </Form.FormField>
                                        );
                                    })}
                                </StyledFieldsGrid>
                            )}
                        </StyledExpandedContent>
                    ))}
                </StyledBenefitPerLifeSelectionModalBodyStepTwo>
            </StyledBnenefitPerLifeScrollContainer>
        </StyledBenefitPerLifeSelectionModalBody>
    );
};

export default PurchaseAttributesStep2;
